import moment from "moment";
import { getService } from "react-in-angularjs";
import { describeReportTable } from "../ai/reportTableDescriptions";
import { REPORT_FILTER_PERIODS } from "../consts/reportFilterPeriodConsts";
import {
  declareAiCapability,
  provideAiCapability
} from "@medflyt/webapp-react/src/modules/ai-omnibar/ai-capability-registry";

/**
 * Building an advanced report from a plain request.
 *
 * The only capability that asks the model a second, different question. Everything else in the omnibar
 * chooses *what to drive*; this chooses *what a report should contain* - which table it is about,
 * which columns to show, which columns are worth filtering on - and it needs the agency's own schema
 * to do that. So the runner sends this page's tables to /ai_omnibar/build_report and applies whatever
 * comes back through the same handlers a person's clicks go through.
 *
 * The tables are the permission boundary, not just the vocabulary: /reports/schema returns only the
 * entities this member may report on, and the endpoint refuses any column that was not in what it was
 * sent. A member without report permissions never sees this capability at all - the registry filters
 * it out on `permissionKey`.
 *
 * `isLastResort` because a request that an existing page or saved report answers should be answered
 * that way; building something new is what you do when nothing fits.
 */
declareAiCapability({
  id: "advancedReport.create",
  title: "Build an advanced report",
  effect: "prefill",
  description:
    "Lays out a new report in the report builder from a description: picks the table it is about, the columns to show and the columns worth filtering on, using the tables this user is allowed to report on. It fills the builder in and stops - the person reviews it, sets the filter values and runs it. A last resort: use it when the user asks for a new or custom report by name, or when nothing else here can answer them.",
  location: "the report builder, Reports > Advanced",
  scope: "advanced-report-tool",
  permissionKey: "view_reports_section_advanced",
  isLastResort: true,
  params: [
    {
      name: "request",
      type: "text",
      required: true,
      description:
        "What the report should show, in the user's own words - the whole request, not a column name. Pass it through as they said it; the builder has the tables and works out the columns."
    }
  ],
  examples: [
    "build a report of patient cost share invoices by month",
    "I need a custom report showing unpaid cost share payments"
  ]
});


//! @ngInject
export function reportToolMain($rootScope, $scope, $state, $stateParams, $filter, $uibModal, toaster, reportService,uiGridExporterConstants,uiGridExporterService) {
  $scope.report = {
    allowEmpty: true,
    name: '',
    primaryTable: '',
    columns: [],
    filters: []
  };
  $scope.loadingReport = false;
  const sortOptions = ["NONE", "ASC", "DESC"];
  const logicalRelationships = ["AND", "OR"];
  $scope.savedReportId = $stateParams.savedReportId;
  $scope.accessType = $stateParams.accessType;
  $scope.mySavedReportsNames = $stateParams.mySavedReportsNames;

  let releaseAiCapability = null;

  /**
   * Available only once the schema is here: it is both what the model chooses from and the list the
   * server checks its answer against, so a step that ran before it arrived would have nothing to
   * build with.
   */
  const provideBuilderToAi = () => {
    if (releaseAiCapability !== null) {
      return;
    }

    releaseAiCapability = provideAiCapability("advancedReport.create", (args) => {
      // identifiers and uniqueIdentifiers travel too: they are how the report engine decides what
      // can be joined and whether a join multiplies the rows, so without them the model would be
      // guessing at cardinality from column names.
      // The aggregates a column accepts come from the schema's own list for its type, which is also
      // what the tool's own Aggregate dropdown is built from - so "SUM the balance" or "by MONTH" is
      // offered rather than guessed at.
      const operatorsFor = (column) =>
        ($scope.reportsSchema.allowedLogicalOperators ?? [])
          .filter(
            op =>
              op.array === (column.array === true) &&
              (op.columnType ?? []).includes(column.type)
          )
          .map(op => op.logicalOperator);

      const aggregatesFor = (column) =>
        ($scope.reportsSchema.aggregateColumnTypes ?? [])
          .filter(
            agg =>
              agg.parameterType === column.type &&
              agg.parameterArray === (column.array === true)
          )
          .map(agg => agg.aggregateType);

      // One entry per column type instead of two lists on each of six hundred columns: the rules
      // depend only on the type, and repeating them was most of the request body.
      const typeRules = [...new Set(
        ($scope.reportsSchema.tables ?? []).flatMap(table =>
          (table.columns ?? []).map(column => JSON.stringify({
            type: column.type,
            array: column.array === true
          }))
        )
      )]
        .map(key => JSON.parse(key))
        .map(({ type, array }) => ({
          type: type + (array ? "[]" : ""),
          aggregates: aggregatesFor({ type, array }),
          operators: operatorsFor({ type, array })
        }));

      const tables = ($scope.reportsSchema.tables ?? []).map(table => ({
        tableName: table.tableName,
        label: table.tableLabel ?? table.tableName,
        description: describeReportTable(table.tableName),
        identifiers: table.identifiers ?? [],
        uniqueIdentifiers: table.uniqueIdentifiers ?? [],
        columns: (table.columns ?? []).map(column => ({
          name: column.name,
          label: column.label ?? column.name,
          type: column.type,
          array: column.array === true
        }))
      }));

      const Consts = getService("Consts");
      const url =
        Consts.api +
        "agencies/" + $rootScope.agencyId +
        "/agency_members/" + $rootScope.agencyMemberId +
        "/ai_omnibar/build_report";

      // A POST, and allowed: the write guard exempts /ai_omnibar/ because these calls ask questions
      // rather than change anything. Nothing here writes to the agency.
      return getService("$http")
        .post(url, {
          text: String(args.request),
          tables,
          typeRules,
          // The same lists the filter dropdowns are built from, so a period it picks is one the
          // person will actually see selected.
          periods: REPORT_FILTER_PERIODS
        })
        .then(res => applyBuiltReport(res.data));
    });
  };

  /**
   * Sets one filter's operator and value, in the shapes this tool reads them from.
   *
   * Three shapes, not one, because the tool has three: the relative operators keep how many and of
   * what in `timePeriodValue`; "LAST X" and "THIS X" keep the period in `value`; everything else keeps
   * a plain comparison value there, and a date has to be a real Date because that is what the
   * datepicker and the serialiser both expect.
   */
  const applyFilterValue = (filter, choice) => {
    filter.operator = choice.operator;

    const relativeWithCount = ["LAST N X", "NEXT N X", "LAST REL N X", "NEXT REL N X"];
    const relativePeriodOnly = ["LAST X", "THIS X", "NEXT X"];

    if (relativeWithCount.indexOf(choice.operator) !== -1) {
      filter.timePeriodValue = {
        interval: choice.interval ?? 1,
        timePeriod: choice.timePeriod ?? "month"
      };

      // And the mirror of it in `value`, which is what handleTimePeriodChange writes when a person
      // picks the period by hand - "3 MONTHS". The tool reads the period out of timePeriodValue but
      // validates against `value`, so a filter set without this one looks unset to Run Report and
      // the report the assistant just built refuses to run.
      filter.value = `${filter.timePeriodValue.interval} ${filter.timePeriodValue.timePeriod}`;

      return;
    }

    if (relativePeriodOnly.indexOf(choice.operator) !== -1) {
      filter.value = choice.timePeriod ?? choice.value ?? "month";

      return;
    }

    if (choice.operator === "IS NULL" || choice.operator === "NOT IS NULL") {
      return;
    }

    if (choice.value === null || choice.value === undefined) {
      return;
    }

    const isDate = ["DATE", "TIMESTAMP", "TIMESTAMPTZ"].indexOf(filter.type) !== -1;

    if (isDate && /^\d{4}-\d{2}-\d{2}$/.test(String(choice.value))) {
      const [year, month, day] = String(choice.value).split("-").map(Number);

      filter.value = new Date(year, month - 1, day);

      return;
    }

    filter.value = filter.type === "NUMBER" ? Number(choice.value) : String(choice.value);
  };

  /** Puts a built report into the tool the way a person's clicks would. */
  const applyBuiltReport = (built) => {
    return getService("$timeout")(() => {
      if (built.columns.length === 0) {
        throw new Error(built.reply || "I could not build that from the tables you can report on.");
      }

      $scope.handleClearReport();

      if (built.name) {
        $scope.report.name = built.name;
      }

      const findColumn = (choice) =>
        ($scope.reportsSchema.tables ?? [])
          .find(table => table.tableName === choice.tableName)
          ?.columns?.find(column => column.name === choice.name);

      // Primary table first: handleColumnClicked sets it from the first column otherwise.
      if (built.primaryTable) {
        $scope.handleTableMainClicked(built.primaryTable);
      }

      for (const choice of built.columns) {
        const column = findColumn(choice);

        if (column === undefined) {
          continue;
        }

        if (!column.selected) {
          $scope.handleColumnClicked(choice.tableName, column);
        }

        // On the copy in report.columns, not on the schema's column: that copy is what the Aggregate
        // dropdown is bound to, and what the report is built from.
        if (choice.aggregate) {
          const added = $scope.report.columns.find(
            candidate => candidate.name === choice.name && candidate.tableName === choice.tableName
          );

          if (added !== undefined) {
            added.aggregate = choice.aggregate;
          }
        }
      }

      for (const choice of built.filters) {
        const column = findColumn(choice);

        if (column === undefined) {
          continue;
        }

        if (!column.filterSelected) {
          $scope.handleFilterClicked(choice.tableName, column);
        }

        if (choice.operator) {
          const added = $scope.report.filters.find(
            candidate => candidate.name === choice.name && candidate.tableName === choice.tableName
          );

          if (added !== undefined) {
            applyFilterValue(added, choice);
          }
        }
      }

      const shown = $scope.report.columns.length;
      const filtered = $scope.report.filters.length;
      const aggregated = $scope.report.columns.filter(column => column.aggregate).length;
      const set = $scope.report.filters.filter(filter => filter.operator).length;

      return (
        (built.reply || "Built a report") +
        " (" + shown + " column" + (shown === 1 ? "" : "s") +
        (aggregated === 0 ? "" : ", " + aggregated + " aggregated") +
        (filtered === 0
          ? ""
          : ", " + filtered + " filter" + (filtered === 1 ? "" : "s") +
            (set === 0 ? "" : " (" + set + " filled in)")) +
        ") - review it and press Run"
      );
    });
  };

  $scope.$on("$destroy", function () {
    if (releaseAiCapability !== null) {
      releaseAiCapability();
      releaseAiCapability = null;
    }
  });

  function initialize() {
    if ($scope.savedReportId === null) {
      reportService.getReportsSchema().then(res => {
        $scope.reportsSchema = res;
        provideBuilderToAi();
      });
    } else {
      reportService.getSavedReportSchema().then(res => {
        $scope.reportsSchema = res;
        handleLoadSavedReport($scope.savedReportId);
        provideBuilderToAi();
      });
    }
  }

  function getAggregatesByType (columnType, isArray) {
    return $scope.reportsSchema.aggregateColumnTypes.filter(aggType => aggType.parameterType === columnType && aggType.parameterArray === isArray);
  }

  function getLogicalOperatorsByType (columnType, isArray) {
    return $scope.reportsSchema.allowedLogicalOperators.filter(operator => operator.array === isArray && operator.columnType.includes(columnType));
  }

  $scope.handleTableMainClicked = (tableName) => {
    $scope.report.primaryTable = tableName;
  }
  
  $scope.handleRefreshTables = () => {
    $scope.report.tables= getTablesArrayByColumns($scope.report.columns);
  }
  $scope.handleClearReport = () => {
    $scope.report = {
      allowEmpty: true,
      name: '',
      primaryTable: '',
      columns: [],
      filters: []
    };
    for (const table of $scope.reportsSchema.tables) {
      for (const column of table.columns) {
        delete column.selected;
        delete column.filterSelected;
      }
    }
  }

  $scope.handleColumnClicked = (tableName, column) => {
    column.selected = !column.selected;
    const existsIndex = $scope.report.columns.findIndex(
      (existsColumn) =>
        existsColumn.name === column.name &&
        existsColumn.tableName === tableName
    );
    // Add if not exists remove if exists
    if (existsIndex === -1) {
      column.selected = true;
      $scope.report.columns.forEach(existsColumn => {
        if (existsColumn.name === column.name) {
          if (existsColumn.label === $filter("camelCase")(existsColumn.name)) {
            existsColumn.label = `${$filter("camelCase")(existsColumn.name)} (${existsColumn.tableName})`;
          }
          column.label = `${$filter("camelCase")(column.name)} (${tableName})`;
        }
      });
      // First column sets as primary table
      if (!$scope.report.primaryTable) {
        $scope.report.primaryTable = tableName;
      }
      $scope.report.columns.push({
        ...column,
        tableName: tableName,
        aggregateColumnTypes: getAggregatesByType(column.type, column.array),
        sortOptions: sortOptions,
        order: sortOptions[0]
      });
      
    } else {
      $scope.report.columns.splice(existsIndex, 1);
    }
    $scope.handleRefreshTables();
    
  };

  $scope.handleFilterClicked = ( tableName, filter ) => {
    filter.filterSelected = !filter.filterSelected;

    const existsIndex = $scope.report.filters.findIndex(existsFilter => existsFilter.name === filter.name && existsFilter.tableName === tableName);
    // Add if not exists remove if exists
    if (existsIndex === -1) {
      $scope.report.filters.forEach(existsFilter => {
        if (existsFilter.name === filter.name) {
          if (existsFilter.label === $filter("camelCase")(existsFilter.name)) {
            existsFilter.label = `${$filter("camelCase")(existsFilter.name)} (${existsFilter.tableName})`;
          }
          filter.label = `${$filter("camelCase")(filter.name)} (${tableName})`;
        }
      });
      $scope.report.filters.push({
        ...filter,
        tableName: tableName,
        logicalOperators: getLogicalOperatorsByType(filter.type, filter.array),
        logicalRelationships: logicalRelationships,
        logicalRelationshipWithNextFilter: logicalRelationships[0], // Default to And
        isLocked: false
      });
    } else {
      $scope.report.filters.splice(existsIndex, 1);
    }
  };

  /**
   * Add filter duplication with un-filled fields
   * Add isCloned flag
   */
  $scope.handleFilterClone = (filter) => {
    $scope.report.filters.push({
      ...filter,
      isCloned: true,
      timePeriodValue: {},
      operator: '',
      value: ''
    });
  }

  /**
   * Remove filter by index - for now only used for cloned filters.
   */
  $scope.handleRemoveFilter = (index, tableName, columnName) => {
    $scope.reportsSchema.tables.find(table => table.tableName === tableName).columns.find(column => column.name === columnName).filterSelected = false;
    $scope.report.filters.splice(index, 1);
  }
  $scope.handleColumnNotInAggFilters = (tableName,column) => {
    if ($scope.report.filters){
    for (var i = 0; i < $scope.report.filters.length; i++) {
      let filter = $scope.report.filters[i];
      if(filter.aggregate && filter.name === column.name && tableName === filter.tableName){
        return false;
      }
    }
  }
    return true;

}
$scope.handleColumnInAggFilters = (tableName,column) => {
  if ($scope.report.filters){
  for (var i = 0; i < $scope.report.filters.length; i++) {
    let filter = $scope.report.filters[i];
    if(column.aggregate && filter.name === column.name && tableName === filter.tableName){
      return false;
    }
  }

}
return true;
}
  $scope.handleSaveReport = () => {
    if (!$scope.report.name) {
      toaster.pop("warning", "Report name is required");
      return;
    } else if ($scope.savedReportId === null && $scope.mySavedReportsNames.includes($scope.report.name)) {
      toaster.pop("warning", "Report name already exists");
      return;
    } else if ($scope.report.filters.filter(filter => isValueEmpty(filter.value, filter.operator)).length > 0) {
        toaster.pop("warning", "There are empty filter values");
        return;
    }

    const currentReport = angular.copy($scope.report);
    const reportToSave = {
      name: currentReport.name,
      reportTemplate: {
        ...currentReport,
        primaryTable: { tableName: currentReport.primaryTable, columns: [] },
        tables: getTablesArrayByColumns(currentReport.columns),
        columns: parseColumns(currentReport.columns),
        filters: parseFilters(currentReport.filters)
      },
    };

    if ($scope.savedReportId === null) {
      reportService.createSavedReport(reportToSave).then(() => {
        toaster.pop("success", "Success", "Successfully created report");
      }).catch(() => {
        toaster.pop("error", "Something went wrong", "Failed to create report");
      });
    } else {
      reportService.editSavedReport($scope.savedReportId, reportToSave).then(() => {
        toaster.pop("success", "Success", "Successfully saved report");
      }).catch(() => {
        toaster.pop("error", "Something went wrong", "Failed to save report");
      });
    }
  }
  $scope.gridApi= null;
  var universalBOM = "\uFEFF";
  $scope.gridOptions = {
    exporterMenuCsv: true,
    exporterMenuPdf: false,
    enableGridMenu: true,
    enableGroupHeaderSelection: true,
    treeRowHeaderAlwaysVisible: false,
    paginationPageSizes: [25, 50, 100],
    paginationPageSize: 50,
    showColumnFooter:false,
    onRegisterApi: function(gridApi){
      $rootScope.gridApi = gridApi;
    },
    enableFiltering: true,
    // Some columns (e.g. "Emergency Contacts", built server-side with jsonb_pretty) hold
    // multi-line values. They render fine on screen, but the embedded newlines break the
    // CSV/Excel export: the cell opens with a blank line and the column reads as empty.
    // Collapse any newlines so each value exports into a single readable cell. This is
    // invoked by ui-grid's exporter for both CSV and Excel.
    exporterFieldCallback: function (grid, row, col, value) {
      if (typeof value === "string" && /[\r\n]/.test(value)) {
        return value.replace(/\s*\r?\n\s*/g, " ").trim();
      }
      return value;
    }

  };

  $scope.handleRunReport = (format= "json") => {
    if ($scope.report.filters.filter(filter => isValueEmpty(filter.value, filter.operator)).length > 0) {
      toaster.pop("warning", "There are empty filter values");
      return;
    }

    $scope.loadingReport = true;
    const currentReport = angular.copy($scope.report);
    const reportToSave = {
      ...currentReport,
      primaryTable: { tableName: currentReport.primaryTable, columns: [] },
      tables: getTablesArrayByColumns(currentReport.columns),
      columns: parseColumns(currentReport.columns),
      filters: parseFilters(currentReport.filters),
      format: format,
      timezone: moment.tz.guess(true)
    };
    if(reportToSave.columns.length > 0) {
      let columnDefs = [];
      for (let i = 0; i < reportToSave.columns; i++) {
        let col = reportToSave.columns[i];
        columnDefs.push({ name: col.label });
      }
      $scope.gridOptions.columnDefs = columnDefs;
    }

    reportService.executeReport(reportToSave).then(({ lines }) => {

      $scope.gridOptions.exporterExcelFilename = getFileName(reportToSave.name,"xslx");
      $scope.gridOptions.exporterCsvFilename = getFileName(reportToSave.name);
      $scope.gridOptions.data=lines;

      toaster.pop("success", "Success", "Successfully created report");
    }).catch(() => {
      toaster.pop("error", "Something went wrong", "Failed to create report");
    }).finally(() => {
      $scope.loadingReport = false;
    });
  }

  const getFileName = (filename,format = "csv") => {
    return `medflyt-report-${(filename ? filename + "-" : '')}${$filter("date")(new Date(), "yyyy-MM-dd")}.${format}`;
  }

  const parseColumns = (columns) => {
    return columns.map((column) => ({
      ...column,
      columnName: column.name,
      columnLabel: column.label,
      aggregate: !column.aggregate ? null : column.aggregate
    }));
  };

  const parseFilters = (filters) => {
    filters.forEach((item, i) => {
      if (item['aggregate']==undefined){
        filters[i].aggregate = null
      }

    });

    return filters.map((filter) => ({
      ...filter,
      tableColumn: filter.name,
      // aggregate: null,
      isLocked: filter.isLocked,
      value: parseFilterValueToObject(filter)
    }));
  };

  const parseFilterValueToObject = (filter) => {
    let valueString = parseFilterValueString(filter);
    let valueObj = {};

    switch (filter.operator) {
      case ("LAST X"):
      case ("THIS X"):
      case ("NEXT X"): {
        valueObj = {
          type: "timePeriod",
          timePeriod: valueString
        };
        break;
      }
      case ("LAST N X"):
      case ("NEXT N X"): {
        valueObj = {
          type: "timePeriodInterval",
          value: {
            interval: filter.timePeriodValue.interval,
            timePeriod: filter.timePeriodValue.timePeriod
          }
        };
        break;
      }
      case ("LAST REL N X"):
      case ("NEXT REL N X"): {
        valueObj = {
          type: "timePeriodNowRelative",
          value: {
            interval: filter.timePeriodValue.interval,
            timePeriod: filter.timePeriodValue.timePeriod
          }
        };
        break;
      }
      case ("IS NULL"):
      case ("NOT IS NULL"):
        valueObj = {
          type: "string",
          value: ""
        }
        break;
      case ("NOW REL TIME COMPARE"):
        valueObj = {
          type: "timeComparisonNowRelative",
          value: {
            comparison: filter.timePeriodValue.comparison,
            interval: filter.timePeriodValue.interval,
            timePeriod: filter.timePeriodValue.timePeriod
          }
        }
        break;
      default:
        valueObj = {
          type: "string",
          value: valueString
        };
        break;
    }

    return valueObj;
  }

  const parseFilterValueString = (filter) => {
    switch (filter.type) {
      case "TIMESTAMPTZ":
      case "TIMESTAMP":
      case "DATE":
        return $filter("mfShortTime")(filter.value, ['withDate', 'withSeconds'])
      case "TIME":
        return $filter("mfShortTime")(filter.value, ['withSeconds'])

      default:
        return "" + filter.value;
    }
  }

  const getTablesArrayByColumns = (columns) => {
    return Array.from(
      new Set(columns.map((column) => column.tableName))
    ).map((tableName) => ({
      tableName: tableName,
      columns: [],
    }))
  }

  const handleLoadSavedReport = (reportId) => {
    if (!reportId) {
      return;
    }
    reportService.getSavedReport(reportId).then((loadedSavedReport) => {
      $scope.report = {
        ...loadedSavedReport.reportTemplate,
        id: loadedSavedReport.id,
        name: loadedSavedReport.name,
        primaryTable: loadedSavedReport.reportTemplate.primaryTable.tableName,
      };
      for (const filter of loadedSavedReport.reportTemplate.filters) {
        filter.value = parseSavedFilterValueToValue(filter);
      }
      for (const table of $scope.reportsSchema.tables) {
        for (const column of table.columns) {
          column.selected =
            loadedSavedReport.reportTemplate.columns.findIndex(
              (templateColumn) =>
                table.tableName === templateColumn.tableName &&
                column.name === templateColumn.columnName
            ) !== -1;
          column.filterSelected =
            loadedSavedReport.reportTemplate.filters.findIndex(
              (templateFilter) =>
                table.tableName === templateFilter.tableName &&
                column.name === templateFilter.tableColumn
            ) !== -1;
        }
      }
    }).catch(err => toaster.pop("error", "Something went wrong", "Failed to load saved report"));
  }

  const parseSavedFilterValueToValue = (filter) => {
    if (filter.type === "NUMBER") {
        return (Number.parseInt(filter.value.value, 10));
    }

    if (filter.type === "BOOLEAN") {
        return (filter.value.value === "true");
    }

    let newValue;

    switch (filter.operator) {
      case ("LAST X"):
      case ("THIS X"):
      case ("NEXT X"): {
        newValue = filter.value.timePeriod;
        break;
      }
      case ("LAST N X"):
      case ("NEXT N X"): 
      case ("LAST REL N X"):
      case ("NEXT REL N X"): {
        newValue = {
            interval: filter.value.interval,
            timePeriod: filter.value.timePeriod
          }
        break;
      }
      case ("NOW REL TIME COMPARE"):
        newValue = {
          comparison: filter.value.comparison,
          interval: filter.value.interval,
          timePeriod: filter.value.timePeriod
        }
        break;
      default:
        newValue = filter.value.value;
        break;
    }

    return newValue;
  }

  const isValueEmpty = (filterValue, filterOperator) => {
      return ((filterValue === undefined || filterValue === "") && !["IS NULL", "NOT IS NULL"].includes(filterOperator));
  }

  $scope.handleFilterDescriptionClick = () => {
    const modalInstance = $uibModal.open({
        templateUrl: 'admin/views/time-period-description-modal.html',
        size: 'md',
        controller: 'timePeriodDescriptionModalCtrl',
        windowClass: 'modal modal-slide-in-right time-period-description-container',
        backdrop: 'static',
        keyboard : false
    });
  };

  $scope.handleBackToReports = () => {
    $state.go('app.reports.savedReports');
  }

  $scope.isAdmin = $rootScope.user.admin;

  initialize();
};
